import { useRef, useState } from 'react'
import { t } from '../i18n/index.js'
import { compressImage, formatBytes, type CapturedPhoto } from '../lib/camera.js'

/**
 * One camera slot. Deliberately a plain `<input capture>`: it opens the phone's
 * own camera app, which is the interaction every worker already knows, and it
 * needs no permissions dance or getUserMedia fallbacks.
 */
export function PhotoSlot({
  label,
  photo,
  onChange,
}: {
  label: string
  photo: CapturedPhoto | null
  onChange: (photo: CapturedPhoto | null) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  async function pick(file: File | undefined) {
    if (!file) return
    setBusy(true)
    try {
      if (photo) URL.revokeObjectURL(photo.previewUrl)
      onChange(await compressImage(file))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`wk-photo-slot ${photo ? 'filled' : ''}`}>
      {photo ? (
        <>
          <img src={photo.previewUrl} alt={label} />
          <button type="button" className="retake" onClick={() => inputRef.current?.click()}>
            {t('task.photo_retake')}
          </button>
        </>
      ) : (
        <>
          <div className="slot-label">{label}</div>
          {busy ? (
            <div className="busy">{t('task.photo_processing')}</div>
          ) : (
            <button type="button" className="wk-btn" onClick={() => inputRef.current?.click()}>
              📷 {t('task.photo_take')}
            </button>
          )}
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(event) => void pick(event.target.files?.[0])}
      />
      {photo && photo.originalBytes > photo.blob.size && (
        <div className="small muted" style={{ padding: '6px 10px' }}>
          {formatBytes(photo.originalBytes)} → {formatBytes(photo.blob.size)}
        </div>
      )}
    </div>
  )
}

/**
 * Several photos of one kind (vorher, nachher, sonstige). Each shot opens the
 * camera again; a thumbnail with a cross removes one. An optional set stays a
 * single small button until the first photo, so simple tasks stay simple.
 */
export function PhotoSet({
  label,
  photos,
  onChange,
  required,
  max = 10,
}: {
  label: string
  photos: CapturedPhoto[]
  onChange: (photos: CapturedPhoto[]) => void
  required: boolean
  max?: number
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  async function pick(files: FileList | null) {
    if (!files || files.length === 0) return
    setBusy(true)
    try {
      const added: CapturedPhoto[] = []
      for (const file of [...files].slice(0, max - photos.length)) added.push(await compressImage(file))
      onChange([...photos, ...added])
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  function remove(index: number) {
    URL.revokeObjectURL(photos[index]!.previewUrl)
    onChange(photos.filter((_, i) => i !== index))
  }

  const input = (
    <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={(event) => void pick(event.target.files)} />
  )

  if (!required && photos.length === 0) {
    return (
      <div className="wk-photo-set optional">
        <button type="button" className="wk-btn ghost" disabled={busy} onClick={() => inputRef.current?.click()}>
          {busy ? t('task.photo_processing') : `📷 ${label} ${t('task.photo_optional')}`}
        </button>
        {input}
      </div>
    )
  }

  return (
    <div className={`wk-photo-set ${photos.length > 0 ? 'filled' : ''}`}>
      <div className="slot-label">
        {label}
        {photos.length > 0 && <span className="count">{photos.length}</span>}
      </div>
      {photos.length > 0 && (
        <div className="wk-photo-grid">
          {photos.map((photo, index) => (
            <div key={photo.previewUrl} className="wk-photo-thumb">
              <img src={photo.previewUrl} alt={`${label} ${index + 1}`} />
              <button type="button" className="remove" aria-label={t('task.photo_remove')} onClick={() => remove(index)}>
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      {busy ? (
        <div className="busy">{t('task.photo_processing')}</div>
      ) : (
        photos.length < max && (
          <button type="button" className={`wk-btn ${photos.length > 0 ? 'ghost' : ''}`} onClick={() => inputRef.current?.click()}>
            📷 {photos.length > 0 ? t('task.photo_more') : t('task.photo_take')}
          </button>
        )
      )}
      {input}
    </div>
  )
}
